import Link from "next/link"
import { useTranslations } from "next-intl"
import { getCountryName } from "@forestsync/db"
import {
  TbLeaf,
  TbMapPin,
  TbRuler,
  TbCalendar,
  TbX,
  TbExternalLink,
  TbArrowRight,
  Card,
  Text,
  Badge,
  Button,
  ActionIcon,
  Anchor,
  Group,
} from "@forestsync/ui"
import type { CarbonSinkWithGeo } from "@/types/glassroom"

const MARKETPLACE_URL = process.env.NEXT_PUBLIC_MARKETPLACE_URL ?? "http://localhost:4102"

interface ForestDetailsCardProps {
  sink: CarbonSinkWithGeo
  onClose: () => void
}

export function ForestDetailsCard({ sink, onClose }: ForestDetailsCardProps) {
  const t = useTranslations();

  return (
    <Card
      padding="md"
      withBorder
      radius="md"
      w={350}
      maw="90vw"
      className="bg-white/75 dark:bg-gray-800/75 backdrop-blur-sm"
    >
      <div className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center">
            <TbLeaf className="mr-2 h-5 w-5 text-forest" />
            <Text fw={700} size="lg">
              {sink.name}
            </Text>
          </div>
          <ActionIcon
            variant="subtle"
            onClick={onClose}
            radius="xl"
            size="sm"
            aria-label="Close"
            className="ml-2"
          >
            <TbX />
          </ActionIcon>
        </div>
        <div className="flex items-center justify-between mt-1">
          <Text
            size="xs"
            className="flex items-center text-gray-700 dark:text-gray-300"
          >
            <TbMapPin className="mr-1 h-3 w-3" />
            {sink.geoLocation
              ? `${sink.geoLocation.lat.toFixed(4)}, ${sink.geoLocation.lng.toFixed(4)}`
              : t("sink.noLocationData")}
          </Text>
          <Group gap={4}>
            {sink.creditType && (
              <Badge variant="light" color="teal" radius="sm" size="xs">
                {sink.creditType}
              </Badge>
            )}
            <Badge variant="outline" radius="sm">
              {getCountryName(sink.country)}
            </Badge>
          </Group>
        </div>
      </div>

      <div className="py-2">
        <div className="space-y-2">
          <div className="flex justify-between">
            <Text
              size="sm"
              className="flex items-center text-gray-700 dark:text-gray-300"
            >
              <TbRuler className="mr-1 h-4 w-4" />
              {t("common.size")}
            </Text>
            <Text size="sm" fw={600} className="text-gray-900 dark:text-white">
              {(sink.sizeHa ?? 0).toLocaleString()} ha
            </Text>
          </div>
          <div className="flex justify-between">
            <Text
              size="sm"
              className="flex items-center text-gray-700 dark:text-gray-300"
            >
              <TbLeaf className="mr-1 h-4 w-4" />
              {t("common.co2Storage")}
            </Text>
            <Text size="sm" fw={600} className="text-gray-900 dark:text-white">
              {(sink.co2Storage ?? 0).toLocaleString()} {t("common.tons")}
            </Text>
          </div>
          {sink.methodology && (
            <div className="flex justify-between">
              <Text
                size="sm"
                className="flex items-center text-gray-700 dark:text-gray-300"
              >
                <TbLeaf className="mr-1 h-4 w-4" />
                {t("sink.methodology")}
              </Text>
              <Text size="sm" fw={600} className="text-gray-900 dark:text-white" lineClamp={1} maw={160}>
                {sink.methodology}
              </Text>
            </div>
          )}
          <div className="flex justify-between">
            <Text
              size="sm"
              className="flex items-center text-gray-700 dark:text-gray-300"
            >
              <TbCalendar className="mr-1 h-4 w-4" />
              {t("common.age")}
            </Text>
            <Text size="sm" fw={600} className="text-gray-900 dark:text-white">
              {t("common.yearsOld", { age: sink.age })}
            </Text>
          </div>
        </div>
      </div>

      <div className="pt-2 flex flex-col gap-2">
        <Link href={`/sink/${sink.refId}`} className="w-full">
          <Button className="w-full" color="forest">
            {t("common.exploreProject")}
          </Button>
        </Link>
        <Anchor
          href={`${MARKETPLACE_URL}/market`}
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
          className="flex items-center justify-center gap-1"
        >
          {t("glassroom.marketData.tradeOnExchange")}
          <TbArrowRight size={14} />
        </Anchor>
      </div>
    </Card>
  );
}
